import { useState } from "react";
import axios from "axios";
import { DashboardLayout } from "@/pages/Dashboard";
import { useLanguage, useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { ShoppingCart, CheckCircle2, Clock } from "lucide-react";
import { toast } from "sonner";

/**
 * Grocery Market — Coming Soon.
 *
 * The marketplace itself is not active yet. While we wait, we collect leads via a
 * lightweight intake form. Every submission is persisted in the `grocery_leads`
 * collection on the backend, and admins can view them via `/api/admin/grocery/leads`.
 *
 * UX rule: never tell the user the service is unavailable until AFTER they submit —
 * the page reads as an early-access waiting list, not an error.
 */
export default function GroceryComingSoon() {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const [form, setForm] = useState({
    name:  user?.name  || "",
    phone: user?.phone || "",
    email: user?.email || "",
    city:  "",
    notes: "",
  });
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim() || !form.city.trim()) {
      toast.error(t("الاسم والهاتف والمدينة حقول مطلوبة.", "Name, phone, and city are required."));
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/grocery/leads`, form);
      setDone(true);
      toast.success(t("تم استلام طلبك بنجاح.", "Your request has been received."));
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : (detail?.message_ar || t("تعذّر إرسال الطلب. حاول مرة أخرى.", "Couldn't submit. Please try again."));
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = "w-full bg-slate-900 border border-slate-700 rounded-xl px-4 py-3 text-white focus:border-yellow-500 outline-none";

  return (
    <DashboardLayout title={t("سوق البقالة", "Grocery Market")}>
      <div className="max-w-3xl space-y-6">
        {/* Coming-soon hero card */}
        <div className="glass-card rounded-2xl p-6 md:p-8 flex items-start gap-4" data-testid="grocery-coming-soon-hero">
          <div className="w-12 h-12 rounded-2xl bg-yellow-500/15 grid place-items-center text-yellow-400 shrink-0">
            <ShoppingCart size={22} />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <h2 className="text-2xl font-bold text-white">{t("سوق البقالة", "Grocery Market")}</h2>
              <span className="px-2 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wide bg-yellow-500/15 text-yellow-300 border border-yellow-500/30 flex items-center gap-1" data-testid="grocery-coming-soon-badge">
                <Clock size={11} /> {t("قريباً", "Coming soon")}
              </span>
            </div>
            <p className="text-slate-300 leading-relaxed">
              {t(
                "نُحضّر تجربة بقالة عربية متكاملة بالتوصيل لباب البيت. سجّل بياناتك أدناه وسنتواصل معك فور إطلاق الخدمة.",
                "We're putting together a complete Arab grocery experience with home delivery. Leave your details below and we'll be in touch the moment the service launches."
              )}
            </p>
          </div>
        </div>

        {done ? (
          /* Thank-you state — keeps the lead visible on screen */
          <div className="glass-card rounded-2xl p-8 text-center space-y-3" data-testid="grocery-lead-success">
            <CheckCircle2 className="mx-auto text-emerald-400" size={56} />
            <h3 className="text-xl font-bold text-white">{t("تم استلام طلبك بنجاح.", "Your request has been received successfully.")}</h3>
            <p className="text-slate-300 max-w-md mx-auto">
              {t(
                "سوق البقالة غير متاح حالياً. سيتم التواصل معك فور إطلاق الخدمة.",
                "The Grocery Market is currently unavailable. We will contact you as soon as the service launches."
              )}
            </p>
          </div>
        ) : (
          <form onSubmit={submit} className="glass-card rounded-2xl p-6 md:p-8 space-y-5" data-testid="grocery-intake-form">
            <h3 className="text-lg font-bold text-white">{t("اطلب الانضمام للقائمة المبكرة", "Request early access")}</h3>

            <div className="grid md:grid-cols-2 gap-4">
              <input
                className={inputCls}
                placeholder={t("الاسم الكامل *", "Full name *")}
                value={form.name}
                onChange={(e) => update("name", e.target.value)}
                required
                data-testid="grocery-name"
              />
              <input
                className={inputCls}
                placeholder={t("رقم الهاتف *", "Phone number *")}
                value={form.phone}
                onChange={(e) => update("phone", e.target.value)}
                required
                data-testid="grocery-phone"
              />
              <input
                className={inputCls}
                type="email"
                placeholder={t("البريد الإلكتروني (اختياري)", "Email (optional)")}
                value={form.email}
                onChange={(e) => update("email", e.target.value)}
                data-testid="grocery-email"
              />
              <input
                className={inputCls}
                placeholder={t("المدينة *", "City *")}
                value={form.city}
                onChange={(e) => update("city", e.target.value)}
                required
                data-testid="grocery-city"
              />
            </div>

            <textarea
              className={inputCls}
              rows={3}
              placeholder={t("ملاحظات (نوع المنتجات اللي تحبّها، أوقات التوصيل، إلخ)", "Notes (products you'd like, delivery times, etc.)")}
              value={form.notes}
              onChange={(e) => update("notes", e.target.value)}
              data-testid="grocery-notes"
            />

            <div className="flex justify-end">
              <Button type="submit" disabled={submitting} className="btn-primary" data-testid="grocery-submit">
                {submitting ? t("جاري الإرسال...", "Submitting...") : t("أرسل الطلب", "Submit request")}
              </Button>
            </div>
          </form>
        )}
      </div>
    </DashboardLayout>
  );
}
